import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import { getAbsoluteURL } from "discourse/lib/get-url";
import { clipboardCopyAsync } from "discourse/lib/utilities";
import DButton from "discourse/ui-kit/d-button";
import DModal from "discourse/ui-kit/d-modal";
import { i18n } from "discourse-i18n";

export default class ShareModal extends Component {
  @service toasts;

  @tracked shareKey = "";

  constructor() {
    super(...arguments);
    this.shareKey = this.args.model.share_key;
  }

  get htmlContext() {
    let context = [];

    this.args.model.context.forEach((post) => {
      context.push(`<p><b>${post.username}:</b></p>`);
      context.push(post.cooked);
    });
    return trustHTML(context.join("\n"));
  }

  get primaryLabel() {
    return this.shareKey
      ? "discourse_ai.ai_bot.share_full_topic_modal.update"
      : "discourse_ai.ai_bot.share_full_topic_modal.share";
  }

  async generateShareURL() {
    try {
      const response = await ajax(
        "/discourse-ai/ai-bot/shared-ai-conversations",
        {
          type: "POST",
          data: {
            topic_id: this.args.model.topic_id,
          },
        }
      );

      const url = getAbsoluteURL(
        `/discourse-ai/ai-bot/shared-ai-conversations/${response.share_key}`
      );
      this.shareKey = response.share_key;

      return new Blob([url], { type: "text/plain" });
    } catch (e) {
      popupAjaxError(e);
      return;
    }
  }

  @action
  async deleteLink() {
    try {
      await ajax(
        `/discourse-ai/ai-bot/shared-ai-conversations/${this.shareKey}.json`,
        {
          type: "DELETE",
        }
      );

      this.shareKey = null;
    } catch (e) {
      popupAjaxError(e);
    }
  }

  @action
  async share() {
    await clipboardCopyAsync(this.generateShareURL.bind(this));
    this.toasts.success({
      duration: "short",
      data: {
        message: i18n("discourse_ai.ai_bot.conversation_shared"),
      },
    });
  }

  @action
  async maybeCopyEmbed(event) {
    if (!event.target.classList.contains("copy-embed")) {
      return true;
    }
    event.stopPropagation();
    event.preventDefault();

    let version = "";
    if (event.target.dataset.artifactVersion) {
      version = `data-ai-artifact-version="${event.target.dataset.artifactVersion}"`;
    }
    const artifactEmbed = `<div class="ai-artifact" ${version} data-ai-artifact-id="${event.target.dataset.artifactId}"></div>`;
    const promise = new Promise((resolve) => {
      resolve(artifactEmbed);
    });

    await clipboardCopyAsync(() => promise);

    this.toasts.success({
      duration: "short",
      data: {
        message: i18n("discourse_ai.ai_bot.embed_copied"),
      },
    });
  }

  <template>
    <DModal
      class="ai-share-full-topic-modal"
      @closeModal={{@closeModal}}
      @title={{i18n "discourse_ai.ai_bot.share_full_topic_modal.title"}}
    >
      <:body>
        {{! eslint-disable ember/template-no-invalid-interactive }}
        <div
          class="ai-share-full-topic-modal__body"
          {{on "click" this.maybeCopyEmbed}}
        >
          {{this.htmlContext}}
        </div>
        {{! eslint-enable }}
      </:body>

      <:footer>
        <DButton
          class="btn-primary confirm"
          @action={{this.share}}
          @icon="copy"
          @label={{this.primaryLabel}}
        />
        {{#if this.shareKey}}
          <DButton
            class="btn-danger"
            @action={{this.deleteLink}}
            @icon="far-trash-can"
            @label="discourse_ai.ai_bot.share_full_topic_modal.delete"
          />
        {{/if}}
      </:footer>
    </DModal>
  </template>
}
